import {
    Alert,
    Button, Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import React, {useEffect, useState} from "react";
import './InboxView.scss';
import '../../../components/common/AppTable.scss';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ForwardIcon from '@mui/icons-material/Forward';
import ReplyIcon from '@mui/icons-material/Reply';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import IconButton from '@mui/material/IconButton';
import {ePismoClientInstance} from "../Service/EPismoClient";
import {useKeycloak} from "@react-keycloak/web";
import InboxConfirming from "./InboxConfirming";
import {useNavigate} from "react-router-dom";

interface InboxDocument {
    id: string;
    documentTypeName: string;
    title: string;
    documentNumber: string;
    sendDate: string;
    senderName: string;
    senderAddress: string;
    senderId?: string;
    receiveDate: boolean;
    attachmentName: string;
    receiptConfirmationRequired: boolean;
    receiptConfirmationNumber?: string;
}

interface InboxDocumentResponse {
    count: number;
    messages: InboxDocument[];
}

interface AttachmentFileNode {
    name: string;
    directory: boolean;
    children: AttachmentFileNode[];
}

export default function InboxView() {

    const [data, setData] = React.useState<InboxDocumentResponse>({count: 0, messages: []});
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState<InboxDocument>({} as InboxDocument);
    const [query, setQuery] = useState("")
    const [offset, setOffset] = useState(0)
    const [loading, setLoading] = useState(false)
    const [attachmentTree, setAttachmentTree] = useState<AttachmentFileNode | null>(null);
    const [attachmentOpen, setAttachmentOpen] = useState(false);
    const [attachmentMessage, setAttachmentMessage] = useState<InboxDocument | null>(null);
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:768px)');

    const keycloak = useKeycloak();

    function load() {
        setLoading(true)
        let params = new URLSearchParams();
        if (query)
            params.set("query", query)
        if (offset)
            params.set("offset", offset.toString())
        ePismoClientInstance.get("/v1/gui/inbox?" + params.toString(), keycloak).then(r => {
            setData(r.data)
            setLoading(false)
        })
    }

    useEffect(() => {
        load();
    }, []);

    function openDocument(message: InboxDocument) {
        setMessage(message);
        if (message.receiveDate) {
            openDocumentForce(message);
        } else {
            setOpen(true);
        }
    }

    function openDocumentForce(message: InboxDocument) {
        ePismoClientInstance.getFile("/v1/gui/inbox/download/" + message.id, keycloak)
            .then(response => {
                const href = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', message.documentNumber + '.pdf'); //or any other extension
                document.body.appendChild(link);
                link.click();
// clean up "a" element & remove ObjectURL
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                load();
                setOpen(false);
            });
    }

    function downloadBlob(blob: Blob, filename: string) {
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    }

    function downloadReceiptConfirmation(message: InboxDocument) {
        ePismoClientInstance.getFile("/v1/gui/inbox/receipt/" + message.id, keycloak)
            .then(response => downloadBlob(response.data, message.receiptConfirmationNumber + '.pdf'));
    }

    function openAttachment(message: InboxDocument) {
        ePismoClientInstance.get("/v1/gui/inbox/attachment/" + message.id + "/files", keycloak)
            .then(r => {
                const tree: AttachmentFileNode = r.data;
                const isSingleFile = !tree.directory || !tree.children || tree.children.length === 0;
                if (isSingleFile) {
                    ePismoClientInstance.getFile("/v1/gui/inbox/attachment/" + message.id, keycloak)
                        .then(response => {
                            downloadBlob(response.data, message.attachmentName);
                            load();
                            setOpen(false);
                        });
                } else {
                    setAttachmentMessage(message);
                    setAttachmentTree(tree);
                    setAttachmentOpen(true);
                }
            });
    }

    function downloadAttachmentFile(message: InboxDocument, pathSegments: string[]) {
        if (pathSegments.length === 0) {
            ePismoClientInstance.getFile("/v1/gui/inbox/attachment/" + message.id, keycloak)
                .then(response => downloadBlob(response.data, message.attachmentName));
            return;
        }
        const directory = pathSegments.map(encodeURIComponent).join("/");
        const filename = pathSegments[pathSegments.length - 1];
        ePismoClientInstance.getFile("/v1/gui/inbox/attachment/" + message.id + "/files?directory=" + directory, keycloak)
            .then(response => downloadBlob(response.data, filename));
    }

    function renderAttachmentNode(node: AttachmentFileNode, path: string[], depth: number): JSX.Element {
        const currentPath = [...path, node.name];
        return (
            <React.Fragment key={currentPath.join("/") + "@" + depth}>
                <div style={{display: 'flex', alignItems: 'center', paddingLeft: depth * 20, paddingTop: 4, paddingBottom: 4}}>
                    {node.directory ? <FolderIcon fontSize="small" sx={{mr: 1}}/> : <InsertDriveFileIcon fontSize="small" sx={{mr: 1}}/>}
                    <span style={{flex: 1, textAlign: 'left'}}>{node.name}</span>
                    <Tooltip title="Pobierz">
                        <IconButton size="small" onClick={() => {
                            if (attachmentMessage) {
                                // For root node download the whole attachment (bez ścieżki)
                                const segments = depth === 0 ? [] : currentPath.slice(1);
                                downloadAttachmentFile(attachmentMessage, segments);
                            }
                        }}>
                            <FileDownloadIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </div>
                {node.children && node.children.map(child => renderAttachmentNode(child, currentPath, depth + 1))}
            </React.Fragment>
        );
    }

    useEffect(() => {
        load();
    }, [offset]);

    return (
        <div className="inboxList">
            <Grid container spacing={3}>
                <Grid xs={12}>
                    <Typography align="left" variant="h1">Skrzynka odbiorcza</Typography>
                </Grid>
                <Grid xs={12}>
                    <div className="searchBar" style={{textAlign: "left"}}>
                        <TextField onChange={e => setQuery(e.target.value)} label="Szukaj" size="small" sx={{mr: 2}}
                                   disabled={loading}/>
                        <Button variant="contained" onClick={load}>
                            {!loading && <><span className="searchBtnText" style={{marginRight: 5}}>Szukaj</span> <SearchIcon/></>}
                            {loading && <>Trwa ładowanie...</>}
                        </Button>
                    </div>
                </Grid>
                <Grid xs={12}>
                    <Grid xs={12}>
                        <Table className="appTable">
                            <TableHead>
                                <TableCell width="30%">Rodzaj dokumentu / Nr sygnatury</TableCell>
                                <TableCell width="12%">Data podpisania</TableCell>
                                <TableCell width="12%">Data odbioru</TableCell>
                                <TableCell width="20%">Nadawca</TableCell>
                                <TableCell width="26%">Akcje</TableCell>
                            </TableHead>
                            <TableBody>
                                {data.messages.map((message) => (
                                    <TableRow key={message.id} className={message.receiveDate ? "" : "unread"}>
                                        <TableCell data-label="Rodzaj dokumentu / Nr sygnatury">
                                            <span
                                                className="link">{message.documentTypeName}</span>
                                            <span>{message.title}</span> {message.documentNumber}
                                        </TableCell>
                                        <TableCell data-label="Data podpisania">{message.sendDate}</TableCell>
                                        <TableCell data-label="Data odbioru">{message.receiveDate ? message.receiveDate : "-"}</TableCell>
                                        <TableCell data-label="Nadawca">
                                            <span>{message.senderName}</span> <span>{message.senderAddress}</span>
                                        </TableCell>
                                        <TableCell data-label="Akcje">
                                            {!message.receiveDate ? (
                                                <Button variant="contained" size="small"
                                                        sx={{
                                                            p: 0,
                                                            mr: 0.5,
                                                            py: 0.5,
                                                            px: 1,
                                                            minWidth: 'auto',
                                                            fontSize: '0.7rem'
                                                        }}
                                                        onClick={e => openDocument(message)}>Otwórz dokument</Button>
                                            ) : (() => {
                                                const actions = [
                                                    <Tooltip title="Otwórz dokument">
                                                        <Button variant="contained" size="small" fullWidth
                                                                sx={{minWidth: 'auto'}}
                                                                onClick={e => openDocument(message)}>
                                                            <FileDownloadIcon fontSize="small"/>
                                                        </Button>
                                                    </Tooltip>,
                                                    ...(message.attachmentName ? [
                                                        <Tooltip title="Pobierz załączniki">
                                                            <Button variant="contained" size="small" fullWidth
                                                                    sx={{minWidth: 'auto'}}
                                                                    onClick={e => openAttachment(message)}>
                                                                <AttachFileIcon fontSize="small"/>
                                                            </Button>
                                                        </Tooltip>
                                                    ] : []),
                                                    ...(message.receiptConfirmationNumber ? [
                                                        <Tooltip title={"Pobierz poświadczenie odbioru nr " + message.receiptConfirmationNumber}>
                                                            <Button variant="contained" size="small" fullWidth
                                                                    sx={{minWidth: 'auto'}}
                                                                    onClick={() => downloadReceiptConfirmation(message)}>
                                                                <AssignmentTurnedInIcon fontSize="small"/>
                                                            </Button>
                                                        </Tooltip>
                                                    ] : []),
                                                    <Tooltip title="Odpowiedz">
                                                        <Button variant="contained" size="small" fullWidth
                                                                sx={{minWidth: 'auto'}}
                                                                onClick={() => navigate('/nadaj-pismo', {state: {replyTo: message}})}>
                                                            <ReplyIcon fontSize="small"/>
                                                        </Button>
                                                    </Tooltip>,
                                                    <Tooltip title="Przekaż">
                                                        <Button variant="contained" size="small" fullWidth
                                                                sx={{minWidth: 'auto'}}
                                                                onClick={() => navigate('/nadaj-pismo', {state: {forwardFrom: message}})}>
                                                            <ForwardIcon fontSize="small"/>
                                                        </Button>
                                                    </Tooltip>,
                                                ];
                                                return (
                                                    <Grid container columns={Math.max(4, actions.length)} spacing={0}>
                                                        {actions.map((action, index) => (
                                                            <Grid key={index} xs={1} sx={{px: 0.5}}>{action}</Grid>
                                                        ))}
                                                    </Grid>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination count={Math.floor(data.count / 10) + 1}
                                    siblingCount={isMobile ? 0 : 1} boundaryCount={1}
                                    onChange={(e, n) => setOffset((n - 1) * 10)}/>
                    </Grid>
                </Grid>
            </Grid>
            <Dialog open={open} className="InboxCofirmingDialog">
                <DialogTitle sx={{textAlign: "center"}}>Potwierdzenie odbioru</DialogTitle>
                <DialogContent sx={{textAlign: "center"}}>
                    {message.receiptConfirmationRequired &&
                        <Alert severity="warning" sx={{textAlign: "left", mb: 2}}>
                            Nadawca zażądał pisemnego poświadczenia odbioru tego pisma. Otwarcie wiadomości jest
                            jednoznaczne z wystawieniem poświadczenia odbioru i przekazaniem go nadawcy.
                        </Alert>}
                    <p>Po otwarciu wiadomości, będzie ona przechowywana przez 3 dni, po czym zostanie usunięta. W
                        systemie pozostanie jedynie sygnatura dokumentu do weryfikacji autentyczności.</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={e => openDocumentForce(message)} color="primary">
                        {message.receiptConfirmationRequired ? "Pobierz dokument i potwierdź odbiór" : "Pobierz dokument"}
                    </Button>
                    <Button onClick={() => setOpen(false)} color="secondary">
                        Nie chcę otwierać dokumentu.
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={attachmentOpen} onClose={() => setAttachmentOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Załączniki</DialogTitle>
                <DialogContent dividers>
                    {attachmentTree && renderAttachmentNode(attachmentTree, [], 0)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAttachmentOpen(false)} color="secondary" variant="contained" sx={{color: '#fff'}}>Zamknij</Button>
                </DialogActions>
            </Dialog>
            <InboxConfirming force={true} onDeclineAdditionalAction={() => window.location.href = '/'}/>
        </div>
    )
}